import React, { useState, useEffect, useCallback } from 'react';
import { AdminLayout } from '../../dashboard/components/AdminLayout';
import { customFieldsService } from '../services/custom-fields.service';
import {
  CustomFieldDefinition,
  CustomFieldDefinitionCreatePayload,
  CustomFieldDefinitionUpdatePayload,
  CustomFieldType,
} from '../../companies/types';
import { COLORS } from '../../../../core/constants/colors';

const FIELD_TYPE_LABELS: Record<CustomFieldType, string> = {
  text: 'Text',
  number: 'Number',
  boolean: 'Boolean (Yes/No)',
  date: 'Date',
  'single-choice': 'Single Choice',
  'multi-choice': 'Multi Choice',
  url: 'URL',
  email: 'Email',
};

const ALL_FIELD_TYPES: CustomFieldType[] = [
  'text',
  'number',
  'boolean',
  'date',
  'single-choice',
  'multi-choice',
  'url',
  'email',
];

const toFieldKey = (name: string): string =>
  name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

interface FieldFormState {
  name: string;
  field_key: string;
  field_type: CustomFieldType;
  description: string;
  placeholder: string;
  options: string;
  is_required: boolean;
  display_order: number;
  is_active: boolean;
}

const EMPTY_FORM: FieldFormState = {
  name: '',
  field_key: '',
  field_type: 'text',
  description: '',
  placeholder: '',
  options: '',
  is_required: false,
  display_order: 0,
  is_active: true,
};

const isChoiceType = (ft: CustomFieldType) =>
  ft === 'single-choice' || ft === 'multi-choice';

export const AdminCustomFieldsPage: React.FC = () => {
  const [definitions, setDefinitions] = useState<CustomFieldDefinition[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editTarget, setEditTarget] = useState<CustomFieldDefinition | null>(null);
  const [form, setForm] = useState<FieldFormState>(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CustomFieldDefinition | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [fieldKeyManuallyEdited, setFieldKeyManuallyEdited] = useState(false);

  const loadDefinitions = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await customFieldsService.getCustomFieldDefinitions(!showInactive);
      setDefinitions(data);
    } finally {
      setIsLoading(false);
    }
  }, [showInactive]);

  useEffect(() => {
    loadDefinitions();
  }, [loadDefinitions]);

  const openAdd = () => {
    setEditTarget(null);
    setForm(EMPTY_FORM);
    setFieldKeyManuallyEdited(false);
    setSaveError(null);
    setShowModal(true);
  };

  const openEdit = (def: CustomFieldDefinition) => {
    setEditTarget(def);
    setForm({
      name: def.name,
      field_key: def.field_key,
      field_type: def.field_type,
      description: def.description ?? '',
      placeholder: def.placeholder ?? '',
      options: def.options ? def.options.join('\n') : '',
      is_required: def.is_required,
      display_order: def.display_order,
      is_active: def.is_active,
    });
    setFieldKeyManuallyEdited(true);
    setSaveError(null);
    setShowModal(true);
  };

  const handleNameChange = (val: string) => {
    setForm((prev) => ({
      ...prev,
      name: val,
      field_key: fieldKeyManuallyEdited ? prev.field_key : toFieldKey(val),
    }));
  };

  const handleFieldKeyChange = (val: string) => {
    setFieldKeyManuallyEdited(true);
    setForm((prev) => ({ ...prev, field_key: val }));
  };

  const handleSave = async () => {
    setSaveError(null);

    const optionsList = isChoiceType(form.field_type)
      ? form.options
          .split('\n')
          .map((s) => s.trim())
          .filter(Boolean)
      : undefined;

    if (isChoiceType(form.field_type) && (!optionsList || optionsList.length === 0)) {
      setSaveError('Options are required for choice field types.');
      return;
    }

    setIsSaving(true);
    try {
      if (editTarget) {
        const payload: CustomFieldDefinitionUpdatePayload = {
          name: form.name || undefined,
          field_type: form.field_type,
          description: form.description || undefined,
          placeholder: form.placeholder || undefined,
          options: optionsList,
          is_required: form.is_required,
          display_order: form.display_order,
          is_active: form.is_active,
        };
        await customFieldsService.updateCustomFieldDefinition(editTarget.id, payload);
      } else {
        const payload: CustomFieldDefinitionCreatePayload = {
          name: form.name,
          field_key: form.field_key || undefined,
          field_type: form.field_type,
          description: form.description || undefined,
          placeholder: form.placeholder || undefined,
          options: optionsList,
          is_required: form.is_required,
          display_order: form.display_order,
        };
        await customFieldsService.createCustomFieldDefinition(payload);
      }
      setShowModal(false);
      await loadDefinitions();
    } catch (err: any) {
      setSaveError(err.message || 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await customFieldsService.deleteCustomFieldDefinition(deleteTarget.id);
      setDeleteTarget(null);
      await loadDefinitions();
    } catch (err: any) {
      alert(err.message || 'Failed to delete');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <AdminLayout pageTitle="Custom Fields" pageDescription="Manage custom company field definitions">
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Custom Fields</h1>
            <p className="text-sm text-gray-500 mt-1">
              Define extra fields that appear on every company's profile.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                className="rounded"
              />
              Show inactive
            </label>
            <button
              onClick={openAdd}
              className="px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors"
              style={{ backgroundColor: COLORS.admin.primary }}
            >
              + Add Field
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg border" style={{ borderColor: '#E5E7EB' }}>
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <div
                className="w-8 h-8 rounded-full border-4 border-t-transparent animate-spin"
                style={{ borderColor: COLORS.admin.primary, borderTopColor: 'transparent' }}
              />
            </div>
          ) : definitions.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <p className="text-sm">No custom fields defined yet.</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs font-medium text-gray-500 uppercase tracking-wide" style={{ borderColor: '#E5E7EB' }}>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Key</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Required</th>
                  <th className="px-4 py-3">Order</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {definitions.map((def) => (
                  <tr
                    key={def.id}
                    className="border-b last:border-0 hover:bg-gray-50 transition-colors"
                    style={{ borderColor: '#F3F4F6' }}
                  >
                    <td className="px-4 py-3 font-medium text-gray-900">{def.name}</td>
                    <td className="px-4 py-3">
                      <code className="px-1.5 py-0.5 text-xs rounded bg-gray-100 text-gray-700 font-mono">
                        {def.field_key}
                      </code>
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {FIELD_TYPE_LABELS[def.field_type] ?? def.field_type}
                    </td>
                    <td className="px-4 py-3">
                      {def.is_required ? (
                        <span className="px-2 py-0.5 text-xs rounded-full bg-red-50 text-red-700">Yes</span>
                      ) : (
                        <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-500">No</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{def.display_order}</td>
                    <td className="px-4 py-3">
                      {def.is_active ? (
                        <span className="px-2 py-0.5 text-xs rounded-full bg-green-50 text-green-700">Active</span>
                      ) : (
                        <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-500">Inactive</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openEdit(def)}
                          className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          Edit
                        </button>
                        {def.is_active && (
                          <button
                            onClick={() => setDeleteTarget(def)}
                            className="text-xs text-red-500 hover:text-red-700 hover:underline"
                          >
                            Deactivate
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Add / Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: '#E5E7EB' }}>
              <h2 className="text-lg font-bold text-gray-900">
                {editTarget ? 'Edit Custom Field' : 'Add Custom Field'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                ✕
              </button>
            </div>
            <div className="px-6 py-4 space-y-4">
              {/* Name */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  placeholder="e.g. ESG Score"
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </div>

              {/* Field Key */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Field Key{' '}
                  {editTarget ? (
                    <span className="text-gray-400 font-normal">(immutable after creation)</span>
                  ) : (
                    <span className="text-gray-400 font-normal">(auto-generated, snake_case)</span>
                  )}
                </label>
                <input
                  type="text"
                  value={form.field_key}
                  onChange={(e) => handleFieldKeyChange(e.target.value)}
                  disabled={!!editTarget}
                  placeholder="e.g. esg_score"
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-200 font-mono disabled:bg-gray-50 disabled:text-gray-400"
                />
              </div>

              {/* Field Type */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Field Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={form.field_type}
                  onChange={(e) => setForm((p) => ({ ...p, field_type: e.target.value as CustomFieldType }))}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-200"
                >
                  {ALL_FIELD_TYPES.map((ft) => (
                    <option key={ft} value={ft}>
                      {FIELD_TYPE_LABELS[ft]}
                    </option>
                  ))}
                </select>
              </div>

              {/* Options — only for choice types */}
              {isChoiceType(form.field_type) && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Options <span className="text-red-500">*</span>{' '}
                    <span className="text-gray-400 font-normal">(one per line)</span>
                  </label>
                  <textarea
                    value={form.options}
                    onChange={(e) => setForm((p) => ({ ...p, options: e.target.value }))}
                    rows={4}
                    placeholder={'Option A\nOption B\nOption C'}
                    className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-200 resize-none"
                  />
                </div>
              )}

              {/* Description */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text"
                  value={form.description}
                  onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                  placeholder="Help text shown to editors"
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </div>

              {/* Placeholder */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Placeholder</label>
                <input
                  type="text"
                  value={form.placeholder}
                  onChange={(e) => setForm((p) => ({ ...p, placeholder: e.target.value }))}
                  placeholder="Input placeholder text"
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </div>

              {/* Display Order */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Display Order</label>
                <input
                  type="number"
                  value={form.display_order}
                  onChange={(e) => setForm((p) => ({ ...p, display_order: parseInt(e.target.value) || 0 }))}
                  className="w-32 px-3 py-2 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </div>

              {/* Checkboxes */}
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={form.is_required}
                    onChange={(e) => setForm((p) => ({ ...p, is_required: e.target.checked }))}
                    className="rounded"
                  />
                  Required
                </label>
                {editTarget && (
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={form.is_active}
                      onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))}
                      className="rounded"
                    />
                    Active
                  </label>
                )}
              </div>

              {saveError && (
                <p className="text-sm text-red-600">{saveError}</p>
              )}
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t" style={{ borderColor: '#E5E7EB' }}>
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-600 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving || !form.name.trim()}
                className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50"
                style={{ backgroundColor: COLORS.admin.primary }}
              >
                {isSaving ? 'Saving…' : editTarget ? 'Save Changes' : 'Create Field'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deactivate Confirmation */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-2">Deactivate Field?</h2>
            <p className="text-sm text-gray-600 mb-6">
              Deactivating <strong>{deleteTarget.name}</strong> will hide it from all company
              profiles. Existing values are preserved and will reappear if the field is re-activated.
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 text-sm font-medium text-gray-600 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-medium text-white rounded-lg bg-red-500 hover:bg-red-600 transition-colors disabled:opacity-50"
              >
                {isDeleting ? 'Deactivating…' : 'Deactivate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};
